/**
 * Title Policy Analyzer — shared domain model.
 *
 * Client-safe: types, the zod schema used by both the server function's
 * validator and the model response parser, severity styling, the
 * "Can I Build It?" checklist derivation and the title objection template.
 */
import { z } from "zod";

export const EXCEPTION_CATEGORIES = [
  "utility",
  "access",
  "setback",
  "drainage",
  "mineral",
  "lien",
  "other",
] as const;

export type ExceptionCategory = (typeof EXCEPTION_CATEGORIES)[number];

export const SEVERITIES = ["high", "medium", "low"] as const;
export type Severity = (typeof SEVERITIES)[number];

export const VERDICTS = ["allowed", "conditional", "blocked"] as const;
export type Verdict = (typeof VERDICTS)[number];

/** Activities the "Can I Build It?" checklist answers for. */
export const BUILD_ACTIVITIES = [
  { id: "pool", label: "Pool", hint: "In-ground or above-ground swimming pool" },
  { id: "fence", label: "Fence", hint: "Perimeter or privacy fencing" },
  { id: "short_term_rental", label: "Short-term rental", hint: "Airbnb / VRBO style rental" },
  { id: "outbuilding", label: "Outbuilding or shed", hint: "Detached shed, barn or garage" },
  { id: "driveway", label: "Driveway or paving", hint: "New drive, parking pad or hardscape" },
  { id: "solar", label: "Solar panels", hint: "Roof or ground-mounted solar" },
  { id: "tree_removal", label: "Tree removal", hint: "Clearing or removing mature trees" },
  { id: "addition", label: "Addition or expansion", hint: "Adding conditioned square footage" },
] as const;

export type ActivityId = (typeof BUILD_ACTIVITIES)[number]["id"];

export const ACTIVITY_IDS = BUILD_ACTIVITIES.map((a) => a.id) as ActivityId[];

export function activityLabel(id: ActivityId): string {
  return BUILD_ACTIVITIES.find((a) => a.id === id)?.label ?? id;
}

export const citationSchema = z.object({
  book: z.string().nullable(),
  page: z.string().nullable(),
  instrument: z.string().nullable(),
  recorded_date: z.string().nullable(),
});

export const buildImpactSchema = z.object({
  activity: z.enum(ACTIVITY_IDS as [ActivityId, ...ActivityId[]]),
  verdict: z.enum(VERDICTS),
  reason: z.string(),
});

export const titleExceptionSchema = z.object({
  number: z.string().nullable(),
  title: z.string(),
  category: z.enum(EXCEPTION_CATEGORIES),
  severity: z.enum(SEVERITIES),
  plain_english: z.string(),
  original_text: z.string(),
  citation: citationSchema,
  actionable_advice: z.string(),
  build_impacts: z.array(buildImpactSchema),
  /**
   * Verbatim phrases from `original_text` that back the plain-English summary.
   * Optional: older saved analyzes and thin model output fall back to
   * `sourceQuotes()`, which derives them from the legal text.
   */
  source_quotes: z.array(z.string()).optional(),
});

export const titleAnalysisSchema = z.object({
  property_address: z.string(),
  risk_level: z.enum(SEVERITIES),
  risk_summary: z.string(),
  exceptions: z.array(titleExceptionSchema),
});

export type TitleCitation = z.infer<typeof citationSchema>;
export type TitleException = z.infer<typeof titleExceptionSchema>;
export type TitleAnalysis = z.infer<typeof titleAnalysisSchema>;

/** Tailwind classes per severity, used by the exception feed and top bar. */
export const SEVERITY_STYLES: Record<
  Severity,
  { label: string; chip: string; border: string; dot: string; band: string }
> = {
  high: {
    label: "High risk",
    chip: "border-risk-high/40 bg-risk-high/10 text-risk-high",
    border: "border-l-4 border-l-risk-high",
    dot: "bg-risk-high",
    band: "bg-risk-high/10 text-risk-high",
  },
  medium: {
    label: "Medium risk",
    chip: "border-risk-medium/40 bg-risk-medium/12 text-risk-medium",
    border: "border-l-4 border-l-risk-medium",
    dot: "bg-risk-medium",
    band: "bg-risk-medium/10 text-risk-medium",
  },
  low: {
    label: "Low risk",
    chip: "border-risk-low/40 bg-risk-low/10 text-risk-low",
    border: "border-l-4 border-l-risk-low",
    dot: "bg-risk-low",
    band: "bg-risk-low/10 text-risk-low",
  },
};

export interface SeverityCounts {
  high: number;
  medium: number;
  low: number;
  total: number;
}

/**
 * Counts by severity. Exceptions whose index is in `dismissed` (the user marked
 * them "not applicable") are excluded from every count.
 */
export function severityCounts(
  analysis: TitleAnalysis,
  dismissed?: ReadonlySet<number>,
): SeverityCounts {
  const counts = { high: 0, medium: 0, low: 0, total: 0 };
  analysis.exceptions.forEach((e, index) => {
    if (dismissed?.has(index)) return;
    counts[e.severity] += 1;
    counts.total += 1;
  });
  return counts;
}

/** "Book 412, Page 88" / "Inst. 2019-004512" / "Recording data not stated". */
export function formatCitation(citation: TitleCitation): string {
  const parts: string[] = [];
  if (citation.book) parts.push(`Book ${citation.book}`);
  if (citation.page) parts.push(`Page ${citation.page}`);
  if (citation.instrument) parts.push(`Inst. ${citation.instrument}`);
  if (!parts.length && citation.recorded_date) parts.push(`Recorded ${citation.recorded_date}`);
  if (!parts.length) return "Recording data not stated";
  return citation.recorded_date && (citation.book || citation.instrument)
    ? `${parts.join(", ")} · Recorded ${citation.recorded_date}`
    : parts.join(", ");
}

export interface ChecklistItem {
  id: ActivityId;
  label: string;
  hint: string;
  verdict: Verdict;
  reason: string;
  /** Indexes into analysis.exceptions that drove this verdict. */
  exceptionIndexes: number[];
}

const VERDICT_RANK: Record<Verdict, number> = { allowed: 0, conditional: 1, blocked: 2 };

/**
 * Rolls per-exception impacts up into one verdict per activity. The worst
 * verdict wins, and the reasons that produced it are kept for the card.
 */
export function buildChecklist(
  analysis: TitleAnalysis,
  dismissed?: ReadonlySet<number>,
): ChecklistItem[] {
  return BUILD_ACTIVITIES.map((activity) => {
    let verdict: Verdict = "allowed";
    const reasons: string[] = [];
    const exceptionIndexes: number[] = [];

    analysis.exceptions.forEach((exception, index) => {
      if (dismissed?.has(index)) return;
      const impact = exception.build_impacts.find((i) => i.activity === activity.id);
      if (!impact || impact.verdict === "allowed") return;
      if (VERDICT_RANK[impact.verdict] > VERDICT_RANK[verdict]) {
        verdict = impact.verdict;
        reasons.length = 0;
        exceptionIndexes.length = 0;
      }
      if (impact.verdict === verdict) {
        reasons.push(impact.reason || exception.title);
        exceptionIndexes.push(index);
      }
    });

    return {
      id: activity.id,
      label: activity.label,
      hint: activity.hint,
      verdict,
      reason: reasons[0] ?? "No recorded exception in this commitment restricts this on its face.",
      exceptionIndexes,
    };
  });
}

/** Copyable title objection letter for a single Schedule B exception. */
export function objectionTemplate(analysis: TitleAnalysis, exception: TitleException): string {
  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const item = exception.number
    ? `Schedule B-II, Item ${exception.number}`
    : "the Schedule B-II exception below";
  return [
    `Date: ${today}`,
    `Re: Title objection: ${analysis.property_address}`,
    "",
    "To the title officer:",
    "",
    `Pursuant to the terms of the purchase agreement, the buyer objects to ${item} of the title commitment for the above property:`,
    "",
    `Exception: ${exception.title}`,
    `Recording: ${formatCitation(exception.citation)}`,
    "",
    `Basis of objection: ${exception.plain_english}`,
    "",
    `Requested cure: ${exception.actionable_advice}`,
    "",
    "Please provide a written response, together with legible copies of the recorded instrument referenced above, prior to the expiration of the objection period.",
    "",
    "Sincerely,",
    "[Buyer name]",
    "",
    "Prepared with Easements.com. Preliminary results only, not a title opinion or legal advice.",
  ].join("\n");
}

/**
 * Inline citations: the exact phrases from the Schedule B legal text that back
 * a plain-English summary. Uses the model's `source_quotes` when present, and
 * otherwise picks the sentences of `original_text` that share the most
 * distinctive words with the plain-English write-up.
 */
export function sourceQuotes(exception: TitleException, limit = 3): string[] {
  const provided = (exception.source_quotes ?? []).map((q) => q.trim()).filter((q) => q.length > 0);
  if (provided.length) return provided.slice(0, limit);

  const sentences = exception.original_text
    .split(/(?<=[.;:])\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 20);
  if (!sentences.length) return [];

  const STOP = new Set([
    "the",
    "and",
    "for",
    "that",
    "this",
    "with",
    "from",
    "into",
    "over",
    "under",
    "your",
    "you",
    "any",
    "all",
    "are",
    "was",
    "were",
    "will",
    "not",
    "but",
    "its",
    "it's",
    "has",
    "have",
    "had",
    "can",
    "cannot",
    "may",
    "must",
    "shall",
    "a",
    "an",
    "of",
    "to",
    "in",
    "on",
    "or",
    "as",
    "at",
    "by",
    "is",
    "be",
  ]);
  const keywords = new Set(
    exception.plain_english
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .split(/\s+/)
      .filter((w) => w.length > 3 && !STOP.has(w)),
  );

  return sentences
    .map((sentence) => {
      const words = sentence
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .split(/\s+/);
      const score = words.reduce((n, w) => (keywords.has(w) ? n + 1 : n), 0);
      return { sentence, score };
    })
    .sort((a, b) => b.score - a.score)
    .filter((row, index) => row.score > 0 || index === 0)
    .slice(0, limit)
    .map((row) => row.sentence);
}

/** Worst severity present drives the overall risk level. */
export function deriveRiskLevel(exceptions: TitleException[]): Severity {
  if (exceptions.some((e) => e.severity === "high")) return "high";
  if (exceptions.some((e) => e.severity === "medium")) return "medium";
  return "low";
}

/**
 * Drops the exceptions the user marked "not applicable" and recomputes the
 * overall risk level so the summary counts and build checklist follow.
 */
export function applyDismissed(
  analysis: TitleAnalysis,
  dismissed: ReadonlySet<number> | number[],
): TitleAnalysis {
  const skip = dismissed instanceof Set ? dismissed : new Set(dismissed);
  if (!skip.size) return analysis;
  const exceptions = analysis.exceptions.filter((_, index) => !skip.has(index));
  return {
    ...analysis,
    exceptions,
    risk_level: exceptions.length ? deriveRiskLevel(exceptions) : "low",
    risk_summary: exceptions.length
      ? `${analysis.risk_summary} (${skip.size} exception${skip.size === 1 ? "" : "s"} marked not applicable.)`
      : "Every exception was marked not applicable, so nothing is scored here.",
  };
}

/**
 * Recomputes the overall risk level and summary from the extracted exceptions.
 *
 * Used by the "Retry risk scoring" action: when extraction succeeded but the
 * scoring pass returned nonsense (or was salvaged with a placeholder summary),
 * this rebuilds the verdict locally without paying for another model call.
 */
export function rescoreAnalysis(analysis: TitleAnalysis): TitleAnalysis {
  const { exceptions } = analysis;
  if (!exceptions.length) {
    return { ...analysis, risk_level: "low", risk_summary: "No Schedule B exceptions were found." };
  }
  const counts = severityCounts(analysis);
  const parts = [
    counts.high ? `${counts.high} high-risk` : "",
    counts.medium ? `${counts.medium} medium-risk` : "",
    counts.low ? `${counts.low} low-risk` : "",
  ].filter(Boolean);
  return {
    ...analysis,
    risk_level: deriveRiskLevel(exceptions),
    risk_summary: `${exceptions.length} Schedule B exception${exceptions.length === 1 ? "" : "s"} reviewed: ${parts.join(", ")}. Review the high and medium items before you buy or build.`,
  };
}
